import { Brand } from "../../models/brand.model";
import { Organization } from "../../models/organization.model";
import { CommandCreateOrganization } from "./commands/cmdCreateOrganization";
import { CommandDeleteOrganization } from "./commands/cmdDeleteOrganization";
import { CommandGetOrganization } from "./commands/cmdGetOrganization";
import { CommandGetDefaultOrganization } from "./commands/cmdGetDefaultOrganization";
import { CommandGetOrganizationBrand } from "./commands/cmdGetOrganizationBrand";
import { CommandGetOrganizations } from "./commands/cmdGetOrganizations";
import { CommandUpdateOrganization } from "./commands/cmdUpdateOrganization";
import { CommandUpdateOrganizationBrand } from "./commands/cmdUpdateOrganizationBrand";
import { BrandPatch, CommandPatchOrganizationBrand } from "./commands/cmdPatchOrganizationBrand";
import { CommandGetPopulationSegments } from "@services/organizations/commands/cmdGetPopulationSegments.ts";
import { ApprovedUrlScope, CommandGetApprovedUrls } from "./commands/cmdGetApprovedUrls";
import { CommandCreateApprovedUrl, CreateApprovedUrlPayload } from "./commands/cmdCreateApprovedUrl";
import { CommandUpdateApprovedUrl, UpdateApprovedUrlPayload } from "./commands/cmdUpdateApprovedUrl";
import { CommandDeleteApprovedUrl } from "./commands/cmdDeleteApprovedUrl";
import { CommandCreatePopulationSegment, CreatePopulationSegmentPayload } from "./commands/cmdCreatePopulationSegment";
import { CommandUpdatePopulationSegment, UpdatePopulationSegmentPayload } from "./commands/cmdUpdatePopulationSegment";
import { CommandDeletePopulationSegment } from "./commands/cmdDeletePopulationSegment";
import { CommandUploadFont } from "../font/commands/cmdUploadFont";
import { CommandLinkBrandFont } from "./commands/cmdLinkBrandFont";
import { CommandUnlinkBrandFont } from "./commands/cmdUnlinkBrandFont";
import { CommandSetBrandDefaultLogo } from "./commands/cmdSetBrandDefaultLogo";
import { CommandAddBrandAsset } from "./commands/cmdAddBrandAsset";
import { CommandReplaceBrandAsset } from "./commands/cmdReplaceBrandAsset";
import { CommandSetBrandAssetTag } from "./commands/cmdSetBrandAssetTag";
import { BrandColorFields, CommandGetBrandColors } from "./commands/cmdGetBrandColors";
import { CommandCreateBrandColor } from "./commands/cmdCreateBrandColor";
import { CommandUpdateBrandColor } from "./commands/cmdUpdateBrandColor";
import { CommandArchiveBrandColor } from "./commands/cmdArchiveBrandColor";
import { BrandTypographyStyleFields, CommandGetBrandTypographyStyles } from "./commands/cmdGetBrandTypographyStyles";
import { CommandCreateBrandTypographyStyle } from "./commands/cmdCreateBrandTypographyStyle";
import { CommandUpdateBrandTypographyStyle } from "./commands/cmdUpdateBrandTypographyStyle";
import { CommandArchiveBrandTypographyStyle } from "./commands/cmdArchiveBrandTypographyStyle";
import { CommandGetBrandAdTokenValues } from "./commands/cmdGetBrandAdTokenValues";
import { CommandGetAvailableBrandAdTokens } from "./commands/cmdGetAvailableBrandAdTokens";
import { CommandListBrandDateFormats } from "./commands/cmdListBrandDateFormats";
import { CommandCreateBrandAdTokenValue } from "./commands/cmdCreateBrandAdTokenValue";
import { CommandUpdateBrandAdTokenValue } from "./commands/cmdUpdateBrandAdTokenValue";
import { CommandArchiveBrandAdTokenValue } from "./commands/cmdArchiveBrandAdTokenValue";
import { BrandAssetFields, CommandCreateBrandAsset } from "./commands/cmdCreateBrandAsset";
import { CommandUpdateBrandAsset } from "./commands/cmdUpdateBrandAsset";
import { CommandArchiveBrandAsset } from "./commands/cmdArchiveBrandAsset";


export class OrganizationService {

    async getOrganizations(organizationId: number, limit=500, offset=0) {
        const cmd = new CommandGetOrganizations();
        const results = await cmd.run(organizationId, limit, offset);
        return results;
    }

    async createOrganization(organization: Organization) {
        const cmd = new CommandCreateOrganization();
        const results = await cmd.run(organization);
        return results;
    }

    async deleteOrganization(organizationId: string, mode: "block" | "reparent" | "cascade" = "block") {
        const cmd = new CommandDeleteOrganization();
        const results = await cmd.run(organizationId, mode);
        return results;
    }

    async getOrganization(organizationId: string) {
        const cmd = new CommandGetOrganization();
        const results = await cmd.run(organizationId);
        return results;
    }

    async getDefaultOrganization() {
        const cmd = new CommandGetDefaultOrganization();
        return await cmd.run();
    }

    async getOrganizationBrand(organizationId: string) {
        const cmd = new CommandGetOrganizationBrand();
        const results = await cmd.run(organizationId);
        return results;
    }

    async updateOrganizationBrand(organizationId: string, brandForm: Brand) {
        const cmd = new CommandUpdateOrganizationBrand();
        const results = await cmd.run(organizationId, brandForm);
        return results;
    }

    async patchOrganizationBrand(organizationId: string, brandId: string, changes: BrandPatch) {
        const cmd = new CommandPatchOrganizationBrand();
        return await cmd.run(organizationId, brandId, changes);
    }

    async getBrandColors(organizationId: string, brandId: string) {
        const cmd = new CommandGetBrandColors();
        return await cmd.run(organizationId, brandId);
    }

    async createBrandColor(organizationId: string, brandId: string, fields: BrandColorFields) {
        const cmd = new CommandCreateBrandColor();
        return await cmd.run(organizationId, brandId, fields);
    }

    async updateBrandColor(organizationId: string, brandId: string, colorId: string, fields: BrandColorFields) {
        const cmd = new CommandUpdateBrandColor();
        return await cmd.run(organizationId, brandId, colorId, fields);
    }

    async archiveBrandColor(organizationId: string, brandId: string, colorId: string) {
        const cmd = new CommandArchiveBrandColor();
        return await cmd.run(organizationId, brandId, colorId);
    }

    async getBrandTypographyStyles(organizationId: string, brandId: string) {
        return await new CommandGetBrandTypographyStyles().run(organizationId, brandId);
    }

    async createBrandTypographyStyle(organizationId: string, brandId: string, fields: BrandTypographyStyleFields) {
        return await new CommandCreateBrandTypographyStyle().run(organizationId, brandId, fields);
    }

    async updateBrandTypographyStyle(organizationId: string, brandId: string, styleId: number, fields: BrandTypographyStyleFields) {
        return await new CommandUpdateBrandTypographyStyle().run(organizationId, brandId, styleId, fields);
    }

    async archiveBrandTypographyStyle(organizationId: string, brandId: string, styleId: number) {
        return await new CommandArchiveBrandTypographyStyle().run(organizationId, brandId, styleId);
    }

    async getBrandAdTokenValues(organizationId: string, brandId: string) {
        return await new CommandGetBrandAdTokenValues().run(organizationId, brandId);
    }

    async getAvailableBrandAdTokens(organizationId: string, brandId: string) {
        return await new CommandGetAvailableBrandAdTokens().run(organizationId, brandId);
    }

    async listBrandDateFormats() {
        return await new CommandListBrandDateFormats().run();
    }

    async createBrandAdTokenValue(organizationId: string, brandId: string, adTokenUuid: string, value: string) {
        return await new CommandCreateBrandAdTokenValue().run(organizationId, brandId, adTokenUuid, value);
    }

    async updateBrandAdTokenValue(organizationId: string, brandId: string, valueUuid: string, value: string) {
        return await new CommandUpdateBrandAdTokenValue().run(organizationId, brandId, valueUuid, value);
    }

    async archiveBrandAdTokenValue(organizationId: string, brandId: string, valueUuid: string) {
        return await new CommandArchiveBrandAdTokenValue().run(organizationId, brandId, valueUuid);
    }

    async updateOrganization(organizationForm: Organization) {
        const cmd = new CommandUpdateOrganization();
        const results = await cmd.run(organizationForm);
        return results;
    }

    async setOrganizationParent(orgId: number, parentId: number | null) {
        const cmd = new CommandUpdateOrganization();
        return await cmd.setParent(orgId, parentId);
    }

    async getPopulationSegments(organizationId: number) {
        const cmd = new CommandGetPopulationSegments();
        const results = await cmd.run(organizationId, 100, 0);
        return results;
    }

    async getApprovedUrls(organizationId: number, scope: ApprovedUrlScope = "own",
                          activeOnly = false, signal?: AbortSignal) {
        const cmd = new CommandGetApprovedUrls();
        return await cmd.run(organizationId, scope, activeOnly, signal);
    }

    async createApprovedUrl(organizationId: number, payload: CreateApprovedUrlPayload) {
        const cmd = new CommandCreateApprovedUrl();
        return await cmd.run(organizationId, payload);
    }

    async updateApprovedUrl(organizationId: number, approvedUrlId: number, payload: UpdateApprovedUrlPayload) {
        const cmd = new CommandUpdateApprovedUrl();
        return await cmd.run(organizationId, approvedUrlId, payload);
    }

    async deleteApprovedUrl(organizationId: number, approvedUrlId: number) {
        const cmd = new CommandDeleteApprovedUrl();
        return await cmd.run(organizationId, approvedUrlId);
    }

    async createPopulationSegment(organizationId: number, payload: CreatePopulationSegmentPayload) {
        const cmd = new CommandCreatePopulationSegment();
        return await cmd.run(organizationId, payload);
    }

    async updatePopulationSegment(organizationId: number, segmentId: number, payload: UpdatePopulationSegmentPayload) {
        const cmd = new CommandUpdatePopulationSegment();
        return await cmd.run(organizationId, segmentId, payload);
    }

    async deletePopulationSegment(organizationId: number, segmentId: number) {
        const cmd = new CommandDeletePopulationSegment();
        return await cmd.run(organizationId, segmentId);
    }

    async uploadFont(file: File) {
        const cmd = new CommandUploadFont();
        return await cmd.run(file);
    }

    async linkBrandFont(organizationId: string, brandId: string, fontId: number) {
        const cmd = new CommandLinkBrandFont();
        return await cmd.run(organizationId, brandId, fontId);
    }

    async unlinkBrandFont(organizationId: string, brandId: string, fontId: number) {
        const cmd = new CommandUnlinkBrandFont();
        return await cmd.run(organizationId, brandId, fontId);
    }

    async setBrandDefaultLogo(organizationId: string, brandId: string, assetId: number) {
        const cmd = new CommandSetBrandDefaultLogo();
        return await cmd.run(organizationId, brandId, assetId);
    }

    async addBrandAsset(organizationId: string, brandId: string, file: File, tag?: string, makeDefault = false) {
        const cmd = new CommandAddBrandAsset();
        return await cmd.run(organizationId, brandId, file, tag, makeDefault);
    }

    async replaceBrandAsset(organizationId: string, brandId: string, assetId: number, file: File) {
        const cmd = new CommandReplaceBrandAsset();
        return await cmd.run(organizationId, brandId, assetId, file);
    }

    async createBrandAsset(organizationId: string, brandId: string, file: File, fields: BrandAssetFields) {
        const cmd = new CommandCreateBrandAsset();
        return await cmd.run(organizationId, brandId, file, fields);
    }

    async updateBrandAsset(organizationId: string, brandId: string, assetId: number, fields: BrandAssetFields) {
        const cmd = new CommandUpdateBrandAsset();
        return await cmd.run(organizationId, brandId, assetId, fields);
    }

    async archiveBrandAsset(organizationId: string, brandId: string, assetId: number) {
        const cmd = new CommandArchiveBrandAsset();
        return await cmd.run(organizationId, brandId, assetId);
    }

    async setBrandAssetTag(organizationId: string, brandId: string, assetId: number, tag: string | null) {
        const cmd = new CommandSetBrandAssetTag();
        return await cmd.run(organizationId, brandId, assetId, tag);
    }
}
